// [Jake UI] - Layout shell with scroll-aware floating header and mobile bottom nav
import React, { useCallback, useEffect, useRef, useState } from "react";
import HeaderSidebar from "./Common/HeaderSidebar/HeaderSidebar";
import FloatingHeader from "./Common/FloatingHeader";
import MobileBottomNav from "./Common/MobileBottomNav";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import { useDashboardShell } from "./PersistentDashboardShell";

const MemoizedFloatingHeader = React.memo(FloatingHeader);
const MemoizedHeaderSidebar = React.memo(HeaderSidebar);

const FloatingHeaderWrapper = ({ renderGitchaDefaultHeader, shouldDisplayFloatingHeader, pinHeader, headerProps, onVisibilityChange }) => {

    const [showFloatingHeader, setShowFloatingHeader] = useState(true);

    const lastScrollY = useRef(window.scrollY);
    const cumulativeDelta = useRef(0);

    useEffect(() => {
        // Skip scroll tracking when header is pinned
        if (pinHeader) return;

        let ticking = false;

        const handleScroll = () => {
            if (ticking) return;
            ticking = true;

            requestAnimationFrame(() => {
                const currentY = window.scrollY;
                const delta = currentY - lastScrollY.current;
                lastScrollY.current = currentY;
                ticking = false;

                // Always show header when near the top
                if (currentY <= 10) {
                    cumulativeDelta.current = 0;
                    setShowFloatingHeader(true);
                    onVisibilityChange?.(true);
                    return;
                }

                // Prevent flicker at bottom of page (elastic scroll bounce)
                const atBottom = (window.innerHeight + currentY) >= (document.body.scrollHeight - 20);
                if (atBottom) return;

                // Accumulate scroll distance in the current direction.
                // Reset when direction changes.
                if ((delta > 0 && cumulativeDelta.current < 0) ||
                    (delta < 0 && cumulativeDelta.current > 0)) {
                    cumulativeDelta.current = 0;
                }
                cumulativeDelta.current += delta;

                // Require meaningful scroll distance before toggling
                if (cumulativeDelta.current > 60) {
                    setShowFloatingHeader(false);
                    onVisibilityChange?.(false);
                } else if (cumulativeDelta.current < -40) {
                    setShowFloatingHeader(true);
                    onVisibilityChange?.(true);
                }
            });
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [pinHeader]);

    if (renderGitchaDefaultHeader) return renderGitchaDefaultHeader

    if (shouldDisplayFloatingHeader) return (
        <div className={`floating-header-wrapper ${showFloatingHeader ? "visible" : "hidden"}`}>
            <MemoizedFloatingHeader {...headerProps} />
        </div>
    )

    return null

}

export const PageWrapper = React.forwardRef((props, ref) => {

    const {
        className = '',
        defaultWebsiteInnerWrapperClassname = 'agent-no-login',
        whiteLabelInnerWrapperClassname = 'agent-no-login-sidebar-spacing',
        headerSidebarProps = {},
        headerProps = {},
        shouldDisplayHeaderSidebar = true,
        shouldDisplayFloatingHeader = true,
        pinHeader = false,
        renderGitchaDefaultHeader = null,
    } = props

    const { isGitchaDefaultWebsite } = useCurrentWebsite()
    const { hasPersistentSidebar } = useDashboardShell()
    const [headerVisible, setHeaderVisible] = useState(true);
    const handleHeaderVisibility = useCallback((visible) => setHeaderVisible(visible), []);

    return (
        <>
            {!isGitchaDefaultWebsite && shouldDisplayHeaderSidebar && !hasPersistentSidebar && <MemoizedHeaderSidebar {...headerSidebarProps} />}
            <div
                ref={ref}
                className={`
                    outer-wrap
                    position-relative
                    ${headerVisible ? '' : 'header-hidden'}
                    ${className}
                    ${isGitchaDefaultWebsite ? defaultWebsiteInnerWrapperClassname : whiteLabelInnerWrapperClassname}`}
            >
                <FloatingHeaderWrapper
                    renderGitchaDefaultHeader={renderGitchaDefaultHeader}
                    shouldDisplayFloatingHeader={isGitchaDefaultWebsite && shouldDisplayFloatingHeader}
                    pinHeader={pinHeader}
                    headerProps={headerProps}
                    onVisibilityChange={handleHeaderVisibility}
                />
                <div> {props?.children} </div>
                {/* [PRD-1] Bottom nav at phone widths - rendered outside FloatingHeaderWrapper so it stays visible on scroll */}
                {isGitchaDefaultWebsite && <MobileBottomNav />}
            </div>
        </>
    )

});